/**
 * Data for the pipeline activity & audit trail (SE-5504).
 *
 * One hook serves BOTH surfaces this ticket ships, because they differ only in
 * scope:
 *   - the per-CARD trail inside the player preview drawer passes a
 *     `kanbanCardId` (AC1/AC2);
 *   - the per-PIPELINE manager log passes only the board id, and therefore
 *     receives every audit event on the pipeline, not just card movements
 *     (AC3, Pietra 2026-07-16).
 *
 * ## All filtering is SERVER-SIDE (SE-6251)
 *
 * SE-6248 added `action` / `actorType` / `actorUserId` to `pipelineActivityLog`,
 * and SE-6251 wires the first two to the per-pipeline log's chip rows. They flow
 * straight into `variables`; NOTHING is narrowed after the response arrives.
 * That is a hard requirement, not a preference — a client-side pass would hide a
 * backend gap and would silently disagree with `totalCount`, which the backend
 * computes from the same filtered query.
 *
 * Two consequences worth stating, because both are load-bearing:
 *
 * - **An empty selection omits the variable entirely** rather than sending
 *   `[]`. Both are documented as "no filter", but only omission is guaranteed by
 *   the argument being nullable; an empty list relies on the resolver treating
 *   `[]` as absent, which is a backend implementation detail this hook should
 *   not depend on. `buildActivityLogVariables` therefore drops the key, and the
 *   key-set guard test asserts it stays dropped.
 * - **`totalCount` and the cursors describe the FILTERED set**, so the header
 *   count and "Load more" both stay correct under a filter with no extra work
 *   here.
 *
 * ## Paging
 *
 * `pipelineActivityLog` has no `relayStylePagination` type policy (adding one
 * would be shared by the card-scoped and board-scoped instances, whose
 * arguments differ), so pages are merged explicitly through `fetchMore` +
 * `updateQuery` — the same shape `RulesPanelContainer` and
 * `usePipelineSwitcherBoards` use. Paging is user-driven ("Load more") rather
 * than an auto-walk: an audit log is unbounded, and the newest page is what a
 * manager opens the surface for.
 * @module features/player-kanban/hooks/usePipelineActivityTrail
 */

import { useCallback, useMemo, useRef, useState } from "react";

import {
  type PipelineActivityAction,
  type PipelineActivityLogQuery,
  type PipelineActivityLogQueryVariables,
  type PipelineActorType,
  useListKanbanOrganizationUsersQuery,
  usePipelineActivityLogQuery,
} from "@/generated/graphql";

import { ACTIVITY_TRAIL_PAGE_SIZE, ORG_USERS_PAGE_SIZE } from "../constants";
import { buildActorNameMap } from "../utils/pipelineActorNames";
import {
  type PipelineActivityRow,
  buildPipelineActivityRows,
} from "../utils/pipelineActivityRows";

/** Arguments for {@link usePipelineActivityTrail}. */
export interface UsePipelineActivityTrailArgs {
  /** Board scope. The query requires it, so a nullish id skips the read. */
  readonly kanbanBoardId?: string | null;
  /** Card scope. Omit for the whole-pipeline log. */
  readonly kanbanCardId?: string | null;
  /**
   * Server-side action filter (SE-6251). Empty or omitted means NO action
   * filter — the variable is left off the request entirely.
   */
  readonly actions?: readonly PipelineActivityAction[];
  /**
   * Server-side actor-type filter (SE-6251). Empty or omitted means NO actor
   * filter — the variable is left off the request entirely.
   */
  readonly actorTypes?: readonly PipelineActorType[];
  /** Skip the reads (e.g. while the surface is closed). */
  readonly skip?: boolean;
}

/**
 * Stable empty defaults. A fresh `[]` per render would be a new object identity
 * every time, which is exactly what makes Apollo's variables churn.
 */
const EMPTY_ACTIONS: readonly PipelineActivityAction[] = [];
const EMPTY_ACTOR_TYPES: readonly PipelineActorType[] = [];

/** Inputs to {@link buildActivityLogVariables}. */
interface ActivityLogVariablesArgs {
  /** Board scope. */
  readonly kanbanBoardId?: string | null;
  /** Card scope, or nullish for the whole-pipeline log. */
  readonly kanbanCardId?: string | null;
  /** Selected actions; empty omits the argument. */
  readonly actions: readonly PipelineActivityAction[];
  /** Selected actor types; empty omits the argument. */
  readonly actorTypes: readonly PipelineActorType[];
}

/**
 * Build the query variables, omitting a filter argument entirely when nothing
 * in that group is selected.
 *
 * The omission is the point: "deselect everything" must mean "no constraint on
 * that group", and the only shape guaranteed to mean that is an ABSENT
 * argument. Sending `[]` would delegate that meaning to the resolver.
 * @param args - {@link ActivityLogVariablesArgs}
 * @param args.kanbanBoardId - Board scope
 * @param args.kanbanCardId - Card scope, or nullish for the whole pipeline
 * @param args.actions - Selected actions; empty omits the argument
 * @param args.actorTypes - Selected actor types; empty omits the argument
 * @returns The variables to issue the query with.
 */
const buildActivityLogVariables = ({
  kanbanBoardId,
  kanbanCardId,
  actions,
  actorTypes,
}: ActivityLogVariablesArgs): PipelineActivityLogQueryVariables => ({
  kanbanBoardId: kanbanBoardId ?? "",
  // `null` (not `undefined`) so the board-scoped and card-scoped instances
  // are distinct, stable cache entries rather than one that alternates.
  kanbanCardId: kanbanCardId ?? null,
  first: ACTIVITY_TRAIL_PAGE_SIZE,
  ...(actions.length > 0 ? { action: [...actions] } : {}),
  ...(actorTypes.length > 0 ? { actorType: [...actorTypes] } : {}),
});

/** Public API of {@link usePipelineActivityTrail}. */
export interface UsePipelineActivityTrailResult {
  /** Every entry received, newest-first. Never filtered client-side. */
  readonly rows: readonly PipelineActivityRow[];
  /** Server-reported total for the scope. */
  readonly totalCount: number;
  /** Whether the first page is still in flight. */
  readonly loading: boolean;
  /** Whether the log read failed. */
  readonly isError: boolean;
  /** Whether the server says more pages exist. */
  readonly hasMore: boolean;
  /** Whether a "Load more" page is in flight. */
  readonly isLoadingMore: boolean;
  /** Fetch and append the next page. No-op when there is none. */
  readonly loadMore: () => void;
}

/**
 * Append a fetched page onto the accumulated connection.
 * @param previous - The accumulated query result.
 * @param next - The newly fetched page, when present.
 * @returns The merged result.
 */
const mergeActivityPages = (
  previous: PipelineActivityLogQuery,
  next: PipelineActivityLogQuery | undefined
): PipelineActivityLogQuery => {
  if (!next?.pipelineActivityLog) return previous;
  return {
    ...previous,
    pipelineActivityLog: {
      ...next.pipelineActivityLog,
      edges: [
        ...(previous.pipelineActivityLog?.edges ?? []),
        ...next.pipelineActivityLog.edges,
      ],
    },
  };
};

/**
 * Read a pipeline's (or one card's) audit trail.
 * @param args - {@link UsePipelineActivityTrailArgs}
 * @param args.kanbanBoardId - Board scope; nullish skips the read
 * @param args.kanbanCardId - Card scope; omit for the whole-pipeline log
 * @param args.actions - Server-side action filter; empty sends no argument
 * @param args.actorTypes - Server-side actor filter; empty sends no argument
 * @param args.skip - Skip the reads while the surface is closed
 * @returns The {@link UsePipelineActivityTrailResult}.
 */
export const usePipelineActivityTrail = ({
  kanbanBoardId,
  kanbanCardId,
  actions = EMPTY_ACTIONS,
  actorTypes = EMPTY_ACTOR_TYPES,
  skip = false,
}: UsePipelineActivityTrailArgs): UsePipelineActivityTrailResult => {
  const isSkipped = skip || !kanbanBoardId;

  const { data, loading, error, fetchMore } = usePipelineActivityLogQuery({
    variables: buildActivityLogVariables({
      kanbanBoardId,
      kanbanCardId,
      actions,
      actorTypes,
    }),
    skip: isSkipped,
  });

  // ONE org-roster read resolves every human actor on the page (see
  // `pipelineActorNames`). Skipped alongside the log so a closed drawer issues
  // nothing; `listMyOrganizationsOrganizationUsers` already carries a
  // relayStylePagination policy, so this shares the app-wide cache entry.
  const { data: orgUsersData } = useListKanbanOrganizationUsersQuery({
    variables: { first: ORG_USERS_PAGE_SIZE },
    skip: isSkipped,
  });

  /**
   * Whether another page is in flight.
   * @remarks
   * The ref is the concurrency lock; the state exists only to drive UI. React
   * state is committed asynchronously, so two `loadMore` calls landing before
   * the commit would both read a stale `false`, both pass the guard, and both
   * call `fetchMore` with the SAME `after` cursor — and `mergeActivityPages`
   * does not de-duplicate, so the trail would render a duplicated page.
   *
   * Same footgun, same fix as `PlayerPreviewDrawerContainer`'s
   * `handleSelectStage` (SE-5494).
   */
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);

  const connection = data?.pipelineActivityLog;
  const pageInfo = connection?.pageInfo;
  const hasMore = pageInfo?.hasNextPage ?? false;
  const endCursor = pageInfo?.endCursor ?? null;

  const actorNames = useMemo(
    () =>
      buildActorNameMap(
        (orgUsersData?.listMyOrganizationsOrganizationUsers?.edges ?? []).map(
          edge => edge.node
        )
      ),
    [orgUsersData]
  );

  const rows = useMemo(
    () =>
      buildPipelineActivityRows(
        (connection?.edges ?? []).map(edge => edge.node),
        actorNames
      ),
    [connection, actorNames]
  );

  const loadMore = useCallback(() => {
    if (!hasMore || !endCursor || isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    void fetchMore({
      variables: { after: endCursor },
      updateQuery: (previous, { fetchMoreResult }) =>
        mergeActivityPages(previous, fetchMoreResult),
    })
      .catch(() => {
        // The surfaced `error` from the base query covers the failure; a
        // rejected page must not become an unhandled rejection.
      })
      .finally(() => {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      });
  }, [fetchMore, hasMore, endCursor]);

  // Deliberately NOT wrapped in `useMemo`. Both consumers read individual
  // fields — the object's identity is never a prop or a hook dependency — so
  // memoising it bought nothing, and routing `loadMore` (which reads a ref)
  // through a factory that runs during render trips `react-hooks/refs`. The
  // expensive members (`rows`, `actorNames`) are memoised individually above,
  // and `loadMore` is a stable `useCallback`.
  return {
    rows,
    totalCount: connection?.totalCount ?? 0,
    loading,
    isError: Boolean(error),
    hasMore,
    isLoadingMore,
    loadMore,
  };
};
